import { a as __rest } from '../../tslib.es6.esm.js';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import React__default from 'react';
import { CheckCircleIcon } from '@iris/react-icons/CheckCircleIcon';
import { ExclamationCircleIcon } from '@iris/react-icons/ExclamationCircleIcon';
import { ExclamationTriangleIcon } from '@iris/react-icons/ExclamationTriangleIcon';
import { InformationCircleIcon } from '@iris/react-icons/InformationCircleIcon';
import { XMarkIcon } from '@iris/react-icons/XMarkIcon';
import { ControlButton } from '../ControlButton/ControlButton.esm.js';
import alertStyles from './Alert.styles.esm.js';
import { cn } from '../../utils/cn.esm.js';

/**
 * Messaging that keeps users informed of important and sometimes time-sensitive changes.
 * Alerts can help to notify users of information or a potential problem that may require their attention.
 */
const Alert = (_a) => {
    var { title, description, children, className, variant = "info", inverse = false, onOpenChange, showCloseButton = true } = _a, props = __rest(_a, ["title", "description", "children", "className", "variant", "inverse", "onOpenChange", "showCloseButton"]);
    const [open, setOpen] = React__default.useState(true);
    // Get the styles from tailwind-variants
    const { base, iconBox, icon, contentBox, content, title: titleStyles, description: descriptionStyles, closeButton, closeIcon, closeButtonBox, titleBox, descriptionBox, } = alertStyles({
        variant,
        inverse,
    });
    // Select the appropriate icon based on the alert type
    const AlertIcon = React__default.useMemo(() => {
        switch (variant) {
            case "success":
                return CheckCircleIcon;
            case "caution":
                return ExclamationTriangleIcon;
            case "error":
                return ExclamationCircleIcon;
            case "info":
            default:
                return InformationCircleIcon;
        }
    }, [variant]);
    // Set the role attribute based on the alert variant
    const alertRole = React__default.useMemo(() => {
        switch (variant) {
            case "error":
                return "alert";
            default:
                return "status";
        }
    }, [variant]);
    // Set the aria-live attribute based on the alert variant
    const alertAriaLive = React__default.useMemo(() => {
        switch (variant) {
            case "error":
                return "assertive";
            default:
                return "polite";
        }
    }, [variant]);
    // It make the component just unmount after the animation ends
    const [shouldRender, setShouldRender] = React__default.useState(open);
    React__default.useEffect(() => {
        if (open) {
            setShouldRender(true);
        }
    }, [open]);
    return (jsx(Fragment, { children: shouldRender && (jsxs("div", Object.assign({ className: cn(base(), className), role: alertRole, "aria-live": alertAriaLive, "data-state": open ? "open" : "closed", onAnimationEnd: open ? undefined : () => setShouldRender(false) }, props, { children: [jsx("div", { className: iconBox(), children: jsx(AlertIcon, { className: icon(), "aria-hidden": "true" }) }), jsx("div", { className: contentBox(), children: jsxs("div", { className: content(), children: [title && (jsx("div", { className: titleBox(), children: jsx("span", { className: titleStyles(), children: title }) })), description && (jsx("div", { className: descriptionBox(), children: jsx("span", { className: descriptionStyles(), children: description }) })), children] }) }), jsx("div", { className: closeButtonBox(), children: showCloseButton && variant !== "error" && (jsx(ControlButton, { className: closeButton(), icon: jsx(XMarkIcon, { className: closeIcon() }), "aria-label": "Dismiss alert", inverse: inverse, onClick: () => {
                            setOpen(false);
                            onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(false);
                        } })) })] }))) }));
};
Alert.displayName = "Alert";

export { Alert };
